import React, { useMemo } from 'react';

import type {
    CellClassParams,
    ColDef,
    ColGroupDef,
    ColumnMenuTab,
    IHeaderParams,
    ITooltipParams,
    ValueFormatterParams,
} from 'ag-grid-community';

import AGCustomHeader from '../../components/ag-table/AGCustomHeader';
import ComponentTypesIconWrapper from '../../components/componentTypesIcon/ComponentTypesIconWrapper';
import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import {
    DATASET_LABEL_CREATE,
    SELECTION_LEFT_STICKY_COLUMN,
} from '../../constants/dataset.table';

import useQueryParams from '../../hooks/helper/useQueryParams';

import { ComponentTypeWithLabelOptions } from '../../models/component-types-model';

import useComponentTypesQuery from '../component-types/useComponentTypesQuery';

import useRoleFlags from '../auth/useRoleFlags';
import useLabelsByComponentTypesQuery from '../labels/useLabelsByComponentTypesQuery';
import useDatasetByIdQuery from './useDatasetByIdQuery';
import useDatasetColumnComponentTypesQuery from './useDatasetColumnComponentTypesQuery';
import useDatasetContentQuery from './useDatasetContentByIdQuery';
import useDatasetLabelColumnsQuery from './useDatasetLabelColumnsQuery';

interface UseGetDatasetGridInfoProps {
    datasetId: number;
    editable?: boolean;
    menuTabs?: ColumnMenuTab[];
}

interface UseGetDatasetGridInfoReturn {
    rowsData: any[];
    columnsConfig: (ColDef | ColGroupDef)[];
    columns: any[];
    allLabels: Record<number, { id: number | null; label_name: string }>;
    isDatasetLoading: boolean;
    isDatasetContentLoading: boolean;
    isColumnComponentTypesLoading: boolean;
    isLabelsByComponentTypeLoading: boolean;
    overAllMissingContentIds: number[];
}

const useGetDatasetGridInfo: (
    props: UseGetDatasetGridInfoProps,
) => UseGetDatasetGridInfoReturn = ({
    datasetId,
    editable = true,
    menuTabs,
}) => {
    const { hasWriteAccess: authWriteAccess } = useRoleFlags();

    const { showMissingContent } = useQueryParams();

    const { data: dataset, isLoading: isDatasetLoading } =
        useDatasetByIdQuery(datasetId);
    const { data: datasetContent, isLoading: isDatasetContentLoading } =
        useDatasetContentQuery(datasetId);

    const { data: componentTypes } = useComponentTypesQuery();
    const {
        data: columnComponentTypes,
        isLoading: isColumnComponentTypesLoading,
    } = useDatasetColumnComponentTypesQuery(datasetId);
    const { data: labelColumns } = useDatasetLabelColumnsQuery(datasetId);

    const labelColumnNameMapping = useMemo<Record<number, string>>(
        () =>
            Object.keys(labelColumns ?? {}).reduce(
                (acc: Record<number, string>, curr) => {
                    const columnName = (dataset?.fields ?? []).find(
                        (item) =>
                            +item.id ===
                            labelColumns?.[curr as unknown as number],
                    );
                    if (columnName) acc[+curr] = columnName.name;
                    return acc;
                },
                {},
            ),
        [labelColumns, dataset],
    );

    const isDatasetArchived = useMemo<boolean>(
        () => !!dataset?.archived_at,
        [dataset],
    );

    const hasWriteAccess = useMemo<boolean>(
        () => authWriteAccess && editable && !isDatasetArchived,
        [authWriteAccess, editable, isDatasetArchived],
    );

    const componentTypeIds = columnComponentTypes
        ? [
              ...new Set(
                  Object.values(columnComponentTypes).filter(
                      (id): id is string => !!id,
                  ),
              ),
          ]
        : undefined;

    const { labelsByComponentType, isLoading: isLabelsByComponentTypeLoading } =
        useLabelsByComponentTypesQuery(componentTypeIds ?? [], {
            enabled: !isColumnComponentTypesLoading,
        });

    const missingContentIds = useMemo<number[]>(() => {
        if (!datasetContent || !labelColumns) return [];

        const labelKeys = new Set<number>(Object.values(labelColumns ?? {}));

        return datasetContent
            .filter(({ content }) =>
                Object.entries(content ?? {}).some(([key, value]) => {
                    if (labelKeys.has(+key)) return false;
                    return !value;
                }),
            )
            .map(({ id }) => id);
    }, [datasetContent, labelColumns]);

    const missingLabelContentIds = useMemo<number[]>(() => {
        const labels = new Set<string>(
            Object.values(labelsByComponentType ?? {}).flatMap((items) =>
                items.map((item) => item.label_name),
            ),
        );

        if (!datasetContent || !labelColumns) return [];

        return datasetContent
            .filter(({ content }) =>
                Object.values(labelColumns).some((columnKey) => {
                    const value = content[columnKey];
                    if (!value) return false;
                    return !labels.has(value);
                }),
            )
            .map(({ id }) => id);
    }, [datasetContent, labelColumns, labelsByComponentType]);

    const overAllMissingContentIds = useMemo<number[]>(
        () =>
            Array.from(
                new Set([...missingContentIds, ...missingLabelContentIds]),
            ),
        [missingContentIds, missingLabelContentIds],
    );

    const rowsData = useMemo(() => {
        if (!datasetContent) return [];

        let content = datasetContent;

        if (showMissingContent === 'true') {
            content = datasetContent.filter((item) =>
                overAllMissingContentIds.includes(item.id),
            );
        }

        return content.map((item) => {
            const baseContent = item.content ?? {};

            // Build label data from item.labels (if any)
            const labelsFromItem = (item.labels ?? []).reduce(
                (acc, { dataset_column, label_id }) => {
                    acc[`label_${dataset_column}`] = label_id;
                    return acc;
                },
                {} as Record<string, number | null>,
            );

            // If labelColumns are defined, map them to corresponding content values
            const labelsFromColumns = labelColumns
                ? Object.entries(labelColumns).reduce(
                      (acc, [dataset_column, labelKey]) => {
                          acc[`label_${dataset_column}`] =
                              baseContent[labelKey];
                          return acc;
                      },
                      {} as Record<string, number | null>,
                  )
                : {};

            // Merge everything into one flattened row object
            return {
                id: item.id,
                ...baseContent,
                ...labelsFromColumns,
                ...labelsFromItem, // have it below than the columns, since if there is a label entity attached to a dataset, it will override the same with the columns
            };
        });
    }, [
        datasetContent,
        labelColumns,
        overAllMissingContentIds,
        showMissingContent,
    ]);

    const labelColumnIds = useMemo<number[]>(
        () => Object.values(labelColumns ?? {}),
        [labelColumns],
    );

    const componentTypesWithLabels = useMemo(() => {
        if (!componentTypes || !labelsByComponentType) return null;
        const componentTypesMap = componentTypes.mapById;

        return Object.entries(componentTypesMap).reduce(
            (acc, [componentTypeId, ct]) => {
                const labels = labelsByComponentType[componentTypeId] ?? [];
                acc[componentTypeId] = {
                    ...ct,
                    labels: [{ id: null, label_name: 'No Label' }, ...labels],
                };
                return acc;
            },
            {} as Record<string, ComponentTypeWithLabelOptions>,
        );
    }, [componentTypes, labelsByComponentType]);

    const columns = useMemo(() => {
        if (!dataset || !componentTypesWithLabels) return [];
        return (dataset.fields ?? []).map((field) => {
            const componentTypeId = columnComponentTypes?.[field.id];
            return {
                ...field,
                componentType:
                    componentTypeId !== undefined
                        ? componentTypesWithLabels[componentTypeId]
                        : null,
            };
        });
    }, [dataset, componentTypesWithLabels, columnComponentTypes]);

    const allLabels = useMemo<
        Record<number, { id: number | null; label_name: string }>
    >(() => {
        if (!componentTypesWithLabels) return [];
        return Object.values(componentTypesWithLabels)
            .flatMap((ct) => ct.labels)
            .reduce((acc, label) => {
                if (label.id !== null) {
                    acc[label.id] = label;
                }
                return acc;
            }, {} as Record<number, { id: number | null; label_name: string }>);
    }, [componentTypesWithLabels]);

    const dataColumns = useMemo<ColDef[]>(() => {
        if (!columns.length) return [];

        return columns
            .filter((col) => isFinite(col.id))
            .flatMap((col) => {
                const hasComponent = Boolean(col.componentType);
                const uniqueValues = Array.from(
                    new Set(
                        (datasetContent ?? [])
                            .map((row) => row.content?.[col.id])
                            .filter(
                                (val): val is string =>
                                    val !== undefined && val !== null,
                            ),
                    ),
                );

                const baseColumn = {
                    headerName: hasComponent
                        ? `${col.componentType!.name}`
                        : col.name,
                    headerGroupComponent: () =>
                        hasComponent ? (
                            <StyledAGCustomHeader>
                                <ComponentTypesIconWrapper
                                    dataKey={col.componentType!.data_key}
                                />
                                {col.componentType!.name}
                            </StyledAGCustomHeader>
                        ) : undefined,
                    field: hasComponent ? `group_${col.id}` : String(col.id),
                    colId: hasComponent ? `group_${col.id}` : String(col.id),
                    resizable: true,
                    editable: !hasComponent && hasWriteAccess,
                    filter: true,
                    filterParams: {
                        values: uniqueValues,
                    },
                    menuTabs: menuTabs,
                    minWidth: 250,
                    hide: labelColumnIds.includes(col.id),
                    cellStyle: (p: CellClassParams) => {
                        if (!hasComponent && !p.value) {
                            return {
                                border: `1px solid ${aampeTheme.colors.orange03}`,
                                backgroundColor:
                                    aampeTheme.colors.warningDisabled,
                            };
                        }
                        return {
                            border: '1px solid transparent',
                            backgroundColor: 'transparent',
                        };
                    },
                };

                if (!hasComponent) {
                    return [baseColumn];
                }

                // ----- Children (only when componentType exists) -----

                const alternateColumn = {
                    headerName: `${col.name} (Alternate)`,
                    headerComponent: (params: IHeaderParams) => (
                        <AGCustomHeader props={params}>
                            <StyledAGCustomHeader>
                                {col.name}
                                <AlternateLabelTag>Alternate</AlternateLabelTag>
                            </StyledAGCustomHeader>
                        </AGCustomHeader>
                    ),
                    headerComponentParams: {
                        enableMenu: true,
                    },
                    field: String(col.id),
                    resizable: true,
                    editable: hasWriteAccess,
                    menuTabs: menuTabs,
                    filter: true,
                    filterParams: {
                        values: uniqueValues,
                    },
                    minWidth: 250,
                    cellStyle: (p: CellClassParams) => {
                        if (!p.value) {
                            return {
                                border: `1px solid ${aampeTheme.colors.orange03}`,
                                backgroundColor:
                                    aampeTheme.colors.warningDisabled,
                            };
                        }
                        return {
                            border: '1px solid transparent',
                            backgroundColor: 'transparent',
                        };
                    },
                };

                const labelColumn = {
                    headerName: `${labelColumnNameMapping[col.id]} (Label)`,
                    headerComponent: (params: IHeaderParams) => (
                        <AGCustomHeader props={params}>
                            <StyledAGCustomHeader>
                                {labelColumnNameMapping[col.id] ? (
                                    <>
                                        {labelColumnNameMapping[col.id]}
                                        <AlternateLabelTag>
                                            Label
                                        </AlternateLabelTag>
                                    </>
                                ) : (
                                    'Label'
                                )}
                            </StyledAGCustomHeader>
                        </AGCustomHeader>
                    ),
                    field: `label_${col.id}`,
                    resizable: true,
                    editable: hasWriteAccess,
                    cellEditor: 'agRichSelectCellEditor',
                    cellDataType: 'text',
                    minWidth: 250,
                    menuTabs: [],
                    tooltipValueGetter: (p: ITooltipParams) => {
                        if (!p.value) return 'No Label';
                        return allLabels[p.value]?.label_name ?? p.value;
                    },
                    cellStyle: (p: CellClassParams) => {
                        const labelMissing = p.value && !allLabels?.[p.value];
                        if (labelMissing) {
                            return {
                                border: `1px solid ${aampeTheme.colors.orange03}`,
                                backgroundColor:
                                    aampeTheme.colors.warningDisabled,
                            };
                        }
                        return {
                            border: '1px solid transparent',
                            backgroundColor: 'transparent',
                        };
                    },
                    valueFormatter: (p: ValueFormatterParams) => {
                        if (!p.value) return 'No Label';
                        return allLabels[p.value]?.label_name ?? p.value;
                    },

                    cellEditorParams: {
                        allowTyping: true,
                        filterList: true,
                        highlightMatch: true,
                        searchType: 'matchAny',

                        values: (p: any) => {
                            const baseValues =
                                col.componentType?.labels
                                    ?.filter((l) => !!l.id)
                                    ?.map((l) => String(l.id)) ?? [];

                            const isInvalidExistingValue =
                                p.value && !allLabels?.[p.value];

                            return isInvalidExistingValue
                                ? [...baseValues, DATASET_LABEL_CREATE]
                                : baseValues;
                        },

                        formatValue: (value: any) => {
                            if (!value) return 'No Label';
                            if (value === DATASET_LABEL_CREATE)
                                return '+ Create Label';
                            return allLabels[value]?.label_name ?? value;
                        },
                    },
                };

                return [
                    {
                        ...baseColumn,
                        children: [alternateColumn, labelColumn],
                    },
                ];
            });
    }, [
        columns,
        datasetContent,
        hasWriteAccess,
        menuTabs,
        labelColumnIds,
        allLabels,
        labelColumnNameMapping,
    ]);

    const selectionColumn = useMemo<ColDef>(
        () => ({
            headerName: '',
            field: SELECTION_LEFT_STICKY_COLUMN,
            checkboxSelection: true,
            headerCheckboxSelection: false,
            pinned: 'left' as const,
            width: 40,
            maxWidth: 40,
            minWidth: 40,
            suppressSizeToFit: true,
            lockPosition: true,
            lockVisible: true,
            resizable: false,
        }),
        [],
    );

    const columnsConfig = useMemo<(ColDef | ColGroupDef)[]>(() => {
        if (hasWriteAccess) {
            return [selectionColumn, ...dataColumns];
        }
        return dataColumns;
    }, [dataColumns, selectionColumn, hasWriteAccess]);

    const data = useMemo<UseGetDatasetGridInfoReturn>(
        () => ({
            rowsData,
            columnsConfig,
            columns,
            allLabels,
            isDatasetLoading,
            isColumnComponentTypesLoading,
            isDatasetContentLoading,
            isLabelsByComponentTypeLoading,
            overAllMissingContentIds,
        }),
        [
            rowsData,
            columnsConfig,
            columns,
            allLabels,
            isDatasetLoading,
            isColumnComponentTypesLoading,
            isDatasetContentLoading,
            isLabelsByComponentTypeLoading,
            overAllMissingContentIds,
        ],
    );

    return data;
};

const AlternateLabelTag = styled.span({
    borderRadius: aampeTheme.spacing4(1),
    padding: aampeTheme.spacing4(0.5, 1),
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    color: aampeTheme.colors.textAccessibleWG01,
});

const StyledAGCustomHeader = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: aampeTheme.spacing4(2),
    gap: aampeTheme.spacing4(2),
});

export default useGetDatasetGridInfo;
